import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useEffect, useState } from 'react'
import { useOutlets, useSaveHall } from '@/api/hooks'
import type { Hall } from '@/api/types'
import { ErrorBanner } from '@/components/common'
import { MeterEditor } from './MeterEditor'
import { SlotEditor } from './SlotEditor'

type Draft = {
  outletId: string
  code: string
  name: string
  floorLocation: string
  seatingCapacity: string
  floatingCapacity: string
  minPax: string
  maxPax: string
  minimumGuarantee: string
  rentalCharge: string
  acAvailable: boolean
  setupBufferMinutes: string
  cleanupBufferMinutes: string
  ebMeterNumber: string
  ebRatePerUnit: string
  ebMultiplyingFactor: string
  cleaningCharge: string
  tentativeEbCharge: string
  description: string
}

const BLANK: Draft = {
  outletId: '',
  code: '',
  name: '',
  floorLocation: '',
  seatingCapacity: '',
  floatingCapacity: '',
  minPax: '',
  maxPax: '',
  minimumGuarantee: '',
  rentalCharge: '',
  acAvailable: true,
  setupBufferMinutes: '120',
  cleanupBufferMinutes: '90',
  ebMeterNumber: '',
  ebRatePerUnit: '',
  ebMultiplyingFactor: '1',
  cleaningCharge: '',
  tentativeEbCharge: '',
  description: '',
}

/**
 * Creates or corrects a bookable space, and ties it to the outlet it belongs to.
 *
 * <p>A banquet hall and a dining area are booked the same way - slots and
 * availability - so one dialog serves both. What is asked of each is not the
 * same, though: a hall is let out, and a dining area is a room in a restaurant
 * that people are seated in.</p>
 */
export function SpaceDialog({
  open,
  space,
  spaceType,
  onClose,
}: {
  open: boolean
  space?: Hall | null
  spaceType: 'HALL' | 'DINING'
  onClose: () => void
}) {
  const { data: outlets } = useOutlets()
  const save = useSaveHall()
  const [draft, setDraft] = useState<Draft>(BLANK)

  // An existing space is whatever it already is; a new one is whichever list
  // it was created from.
  const kind = space?.spaceType ?? spaceType
  const noun = kind === 'DINING' ? 'dining area' : 'hall'

  /**
   * Whether this is a space that is let out.
   *
   * <p>A hall is: it is taken for the day, so it carries a rent, a guarantee of
   * covers to make it worth holding, hours either side to dress and clear it,
   * and its own meter to bill the power against. A dining area is none of that
   * - a party is seated in the restaurant and eats what it orders - and asking
   * for seven numbers that never apply is asking somebody to invent them.</p>
   */
  const letOut = kind !== 'DINING'

  useEffect(() => {
    if (!open) return
    setDraft(
      space
        ? {
            outletId: space.outletId,
            code: space.code,
            name: space.name,
            floorLocation: space.floorLocation ?? '',
            seatingCapacity: String(space.seatingCapacity ?? ''),
            minPax: space.minPax == null ? '' : String(space.minPax),
            maxPax: space.maxPax == null ? '' : String(space.maxPax),
            floatingCapacity: space.floatingCapacity == null ? '' : String(space.floatingCapacity),
            minimumGuarantee: space.minimumGuarantee == null ? '' : String(space.minimumGuarantee),
            rentalCharge: String(space.rentalCharge ?? ''),
            acAvailable: space.acAvailable,
            setupBufferMinutes: String(space.setupBufferMinutes ?? 120),
            cleanupBufferMinutes: String(space.cleanupBufferMinutes ?? 90),
            ebMeterNumber: space.ebMeterNumber ?? '',
            ebRatePerUnit: space.ebRatePerUnit ? String(space.ebRatePerUnit) : '',
            ebMultiplyingFactor: space.ebMultiplyingFactor
              ? String(space.ebMultiplyingFactor)
              : '1',
            cleaningCharge: space.cleaningCharge ? String(space.cleaningCharge) : '',
            tentativeEbCharge: space.tentativeEbCharge ? String(space.tentativeEbCharge) : '',
            description: space.description ?? '',
          }
        : // A hall is dressed and cleared around a booking, so it is held for
          // hours either side. A dining area is turned over between sittings,
          // and giving it a hall's buffers would block the next party for
          // three and a half hours nobody asked for.
          { ...BLANK, setupBufferMinutes: letOut ? '120' : '0',
            cleanupBufferMinutes: letOut ? '90' : '0' },
    )
  }, [open, space, letOut])

  const set = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }))
  const valid = Boolean(draft.outletId && draft.code.trim() && draft.name.trim() && draft.seatingCapacity)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {space ? `Edit ${space.name}` : `New ${noun}`}
      </DialogTitle>
      <DialogContent>
        <ErrorBanner error={save.error} />
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              required
              label="Outlet"
              value={draft.outletId}
              onChange={(event) => set({ outletId: event.target.value })}
              helperText={`The outlet this ${noun} belongs to`}
            >
              {(outlets ?? []).map((outlet) => (
                <MenuItem key={outlet.id} value={outlet.id}>
                  {outlet.code} — {outlet.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              required
              label="Code"
              value={draft.code}
              onChange={(event) => set({ code: event.target.value.toUpperCase() })}
              disabled={Boolean(space)}
              helperText={space ? 'Codes cannot change once used' : 'Unique'}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Floor"
              value={draft.floorLocation}
              onChange={(event) => set({ floorLocation: event.target.value })}
              placeholder="Ground Floor"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Name"
              value={draft.name}
              onChange={(event) => set({ name: event.target.value })}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              required
              type="number"
              label="Seating"
              value={draft.seatingCapacity}
              onChange={(event) => set({ seatingCapacity: event.target.value })}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Floating"
              value={draft.floatingCapacity}
              onChange={(event) => set({ floatingCapacity: event.target.value })}
            />
          </Grid>
          {/* What it is let for, which is not what it seats. A four hundred
              seat hall let to thirty is a room nobody wants to sit in and a
              date that could have been sold. */}
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Fewest guests"
              value={draft.minPax}
              onChange={(event) => set({ minPax: event.target.value })}
              helperText="Blank is no floor"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Most guests"
              value={draft.maxPax}
              onChange={(event) => set({ maxPax: event.target.value })}
              helperText="Blank falls back to the seating"
            />
          </Grid>

          {letOut ? (
            <>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Minimum guarantee"
                  value={draft.minimumGuarantee}
                  onChange={(event) => set({ minimumGuarantee: event.target.value })}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Rental"
                  value={draft.rentalCharge}
                  onChange={(event) => set({ rentalCharge: event.target.value })}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Setup buffer"
                  value={draft.setupBufferMinutes}
                  onChange={(event) => set({ setupBufferMinutes: event.target.value })}
                  helperText="Minutes before"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Cleanup buffer"
                  value={draft.cleanupBufferMinutes}
                  onChange={(event) => set({ cleanupBufferMinutes: event.target.value })}
                  helperText="Minutes after"
                />
              </Grid>
            </>
          ) : null}

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={draft.acAvailable}
                  onChange={(event) => set({ acAvailable: event.target.checked })}
                />
              }
              label="Air conditioned"
            />
          </Grid>
          {/* What the hall itself costs to run while a guest has it: the power
              off its own meter and the charge for clearing it afterwards. Both
              are billed on as amenities at 18%. A dining area's is the
              restaurant's own running cost and is not billed to anybody. */}
          {letOut ? (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Amenities — billed on at 18%, together as one line
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="EB rate per unit"
                  value={draft.ebRatePerUnit}
                  onChange={(event) => set({ ebRatePerUnit: event.target.value })}
                  helperText="Shared by every meter on this hall - each meter's own number and gearing are set below"
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Cleaning charge"
                  value={draft.cleaningCharge}
                  onChange={(event) => set({ cleaningCharge: event.target.value })}
                  helperText="What clearing this hall costs. A slot can override it"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Tentative EB charge"
                  value={draft.tentativeEbCharge}
                  onChange={(event) => set({ tentativeEbCharge: event.target.value })}
                  helperText="Estimated electricity for an event here, printed on the quotation. The bill uses the meter"
                />
              </Grid>
            </>
          ) : null}

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Description"
              value={draft.description}
              onChange={(event) => set({ description: event.target.value })}
            />
          </Grid>

          {/* Slots are set up by hand, per space, once it exists. */}
          {space ? (
            <Grid item xs={12}>
              <SlotEditor space={space} />
            </Grid>
          ) : null}

          {/* Its meters, for the same reason and in the same place: a space
              that does not exist yet has no boards to read. */}
          {space ? (
            <Grid item xs={12}>
              <MeterEditor space={space} />
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Alert severity="info">
                Save the {noun} first, then add the slots it is sold in — there are no default slots.
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!valid || save.isPending}
          onClick={() =>
            save.mutate(
              {
                id: space?.id,
                body: {
                  outletId: draft.outletId,
                  // An existing space keeps whatever it already is; a new one is
                  // whichever list it was created from.
                  spaceType: space?.spaceType ?? spaceType,
                  code: draft.code.trim(),
                  name: draft.name.trim(),
                  description: draft.description || undefined,
                  floorLocation: draft.floorLocation || undefined,
                  seatingCapacity: Number(draft.seatingCapacity || 0),
                  floatingCapacity: draft.floatingCapacity ? Number(draft.floatingCapacity) : undefined,
                  minPax: draft.minPax ? Number(draft.minPax) : undefined,
                  maxPax: draft.maxPax ? Number(draft.maxPax) : undefined,
                  minimumGuarantee: draft.minimumGuarantee ? Number(draft.minimumGuarantee) : undefined,
                  rentalCharge: draft.rentalCharge ? Number(draft.rentalCharge) : 0,
                  acAvailable: draft.acAvailable,
                  setupBufferMinutes: Number(draft.setupBufferMinutes || 0),
                  cleanupBufferMinutes: Number(draft.cleanupBufferMinutes || 0),
                  ebMeterNumber: draft.ebMeterNumber || undefined,
                  ebRatePerUnit: draft.ebRatePerUnit ? Number(draft.ebRatePerUnit) : undefined,
                  ebMultiplyingFactor: draft.ebMultiplyingFactor
                    ? Number(draft.ebMultiplyingFactor)
                    : 1,
                  cleaningCharge: draft.cleaningCharge ? Number(draft.cleaningCharge) : 0,
                  tentativeEbCharge: draft.tentativeEbCharge ? Number(draft.tentativeEbCharge) : 0,
                },
              },
              { onSuccess: onClose },
            )
          }
        >
          {space ? 'Save changes' : `Create ${noun}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
